/**
 * Services catalogue and service detail — the heart of the mock-up's
 * "services, not companies" idea.
 */
import { NBSP } from "@no-human/typografia";
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";
import {
	breadcrumbs,
	chipRow,
	eyebrow,
	markerList,
	pill,
	sectionHead,
} from "../components/common.js";
import { categoryBlock, caseCard, relatedServiceCard } from "../components/cards.js";
import { notFoundView } from "./status.js";

export async function servicesListView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const categories = await ctx.gateway.serviceCategories();

	applyPageSeo(ctx, {
		title: `${copy.nav.services} — ${copy.brand.name}`,
		description: copy.services.lead,
	});

	const view = el("div", { class: "mb-view mb-view--services" });

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--white mb-pagehead" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: copy.nav.services }]),
				el("h1", { class: "mb-pagehead__title" }, copy.services.title),
				el("p", { class: "mb-pagehead__lead" }, copy.services.lead),
			),
		),
	);

	const blocks = el("div", { class: "mb-catblocks" });
	for (const category of categories) blocks.appendChild(categoryBlock(ctx, category));
	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--paper" },
			el("div", { class: "mb-section__inner" }, blocks),
		),
	);

	// A category anchor arrives as /uslugi#kategoria-<slug>; the router does not
	// scroll for us, so honour the fragment once the blocks are in the document.
	const fragment = decodeURIComponent(window.location.hash.replace(/^#/, ""));
	if (fragment) {
		window.requestAnimationFrame(() => {
			const target = document.getElementById(fragment);
			target?.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	}
	void match;

	return view;
}

export async function serviceDetailView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const slug = match.params.slug ?? "";
	const service = await ctx.gateway.service(slug);
	if (!service) return notFoundView(ctx);

	applyPageSeo(ctx, {
		title: `${service.title} — ${copy.brand.name}`,
		description: service.usp,
	});

	const category = service.category;
	const view = el("div", { class: "mb-view mb-view--service" });

	// --- hero ---------------------------------------------------------------
	const heroCopy = el(
		"div",
		{ class: "mb-servicehero__copy" },
		breadcrumbs(ctx, [
			{ label: copy.nav.start, key: "home" },
			{ label: copy.nav.services, key: "services" },
			{ label: category?.title ?? service.title },
		]),
		category ? pill(category.title, "accent") : null,
		el("h1", { class: "mb-servicehero__title" }, service.title),
		el("p", { class: "mb-servicehero__lead" }, service.summary),
		el(
			"div",
			{ class: "mb-servicehero__ctas" },
			el(
				"a",
				{ class: "mb-cta mb-cta--accent", href: ctx.href("contact"), "data-routekey": "contact" },
				copy.serviceDetail.ask,
			),
			el(
				"a",
				{
					class: "mb-cta mb-cta--outline",
					href: ctx.href("services"),
					"data-routekey": "services",
				},
				copy.serviceDetail.allServices,
			),
		),
	);

	const heroCard = el(
		"aside",
		{ class: "mb-servicehero__card" },
		service.heroImage
			? el("img", {
					src: service.heroImage.url,
					alt: service.heroImage.alt ?? service.title,
					loading: "lazy",
					decoding: "async",
				})
			: null,
		el("div", { class: "mb-servicehero__cardbody" }, statFromService(ctx, service)),
	);

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--glow mb-servicehero" },
			el("div", { class: "mb-section__inner mb-servicehero__grid" }, heroCopy, heroCard),
		),
	);

	// --- scope + product groups --------------------------------------------
	const scope = el(
		"div",
		null,
		eyebrow(copy.serviceDetail.scopeEyebrow),
		el("h2", { class: "mb-blocktitle" }, copy.serviceDetail.scopeTitle),
		markerList(scopeItems(service.body, service.usp)),
	);

	const groups = el(
		"div",
		null,
		eyebrow(copy.serviceDetail.groupsEyebrow),
		el("h2", { class: "mb-blocktitle" }, copy.serviceDetail.groupsTitle),
		chipRow(service.materialStreams),
		el(
			"div",
			{ class: "mb-callout" },
			el("h3", { class: "mb-callout__title" }, copy.serviceDetail.notListedTitle),
			el("p", { class: "mb-callout__text" }, copy.serviceDetail.notListedDesc),
			el(
				"a",
				{ class: "mb-cta mb-cta--solid", href: ctx.href("contact"), "data-routekey": "contact" },
				copy.serviceDetail.notListedCta,
			),
		),
	);

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el("div", { class: "mb-section__inner mb-twocol" }, scope, groups),
		),
	);

	// --- process ------------------------------------------------------------
	const steps = el("div", { class: "mb-grid mb-grid--4" });
	for (const step of copy.serviceDetail.process) {
		steps.appendChild(
			el(
				"article",
				{ class: "mb-step" },
				el("span", { class: "mb-step__num" }, step.n),
				el("h3", { class: "mb-step__title" }, step.title),
				el("p", { class: "mb-step__desc" }, step.desc),
			),
		);
	}
	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--paper" },
			el(
				"div",
				{ class: "mb-section__inner" },
				sectionHead(ctx, {
					eyebrow: copy.serviceDetail.processEyebrow,
					title: copy.serviceDetail.processTitle,
				}),
				steps,
			),
		),
	);

	// --- case studies using this service ------------------------------------
	if (service.caseStudies.length > 0) {
		const grid = el("div", { class: "mb-grid mb-grid--3" });
		for (const study of service.caseStudies.slice(0, 3)) grid.appendChild(caseCard(ctx, study));
		view.appendChild(
			el(
				"section",
				{ class: "mb-section mb-section--white" },
				el(
					"div",
					{ class: "mb-section__inner" },
					sectionHead(ctx, {
						title: copy.cases.title,
						link: { label: copy.serviceDetail.relatedAll, key: "caseStudies" },
						className: "mb-sectionhead--row",
					}),
					grid,
				),
			),
		);
	}

	// --- other services in the category ------------------------------------
	const related = (category?.services ?? []).filter((s) => s.slug !== service.slug).slice(0, 3);
	if (related.length > 0) {
		const grid = el("div", { class: "mb-grid mb-grid--3" });
		for (const sibling of related) grid.appendChild(relatedServiceCard(ctx, sibling));
		view.appendChild(
			el(
				"section",
				{ class: "mb-section mb-section--paper" },
				el(
					"div",
					{ class: "mb-section__inner" },
					sectionHead(ctx, {
						title: `${copy.serviceDetail.relatedTitle} ${category?.title ?? ""}`.trim(),
						link: { label: copy.serviceDetail.relatedAll, key: "services" },
						className: "mb-sectionhead--row",
					}),
					grid,
				),
			),
		);
	}

	return view;
}

/** The headline figure card on the service hero. */
function statFromService(
	ctx: AppContext,
	service: Awaited<ReturnType<AppContext["gateway"]["service"]>>,
): HTMLElement {
	const streams = service?.materialStreams.length ?? 0;
	const label = ctx.locale() === "en" ? "product groups we accept" : "grup produktowych w obsłudze";
	return el(
		"div",
		{ class: "mb-stat mb-stat--big" },
		el("span", { class: "mb-stat__value" }, String(streams)),
		el("span", { class: "mb-stat__label" }, label),
	);
}

/**
 * The mock-up's "what exactly we deliver" list. LIVE content delivers the scope
 * as rich text in `body`, so split it into list items; if the body is a single
 * paragraph, fall back to the one-line USP rather than rendering an empty list.
 */
function scopeItems(body: string, usp: string): string[] {
	const items = Array.from(body.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)).map((m) =>
		stripTags(m[1] ?? ""),
	);
	if (items.length > 0) return items;
	const paragraphs = Array.from(body.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi))
		.map((m) => stripTags(m[1] ?? ""))
		.filter((p) => p.length > 0);
	return paragraphs.length > 1 ? paragraphs : [usp];
}

function stripTags(value: string): string {
	return value
		.replace(/<[^>]*>/g, "")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&nbsp;/g, NBSP)
		.trim();
}
